import apiClient from './ApiClient';

import { AxiosResponse, ResponseType } from 'axios';

/*
 * HAT-1343: כשלא מבקשים responseType, axios רץ עם silentJSONParsing - הוא מנסה
 * JSON.parse על הגוף, ואם זה נכשל הוא **בולע את החריגה ומחזיר את המחרוזת
 * הגולמית** עם סטטוס 200. גוף JSON שנקטע באמצע (תשובה גדולה, חיבור שנפל,
 * שכבת CDN) חוזר כך כמחרוזת, react-query שומר אותו ב-cache כאילו הצליח, וכל
 * צרכן שמצפה למערך קורס עליו - שוב ושוב, כי ה-cache שורד כל remount.
 *
 * תשובה שה-Content-Type שלה הוא JSON ושנשארה מחרוזת היא בהגדרה תשובה שלא
 * נפרסרה. נזרוק: react-query יסמן אותה כשגיאה, ינסה שוב, ולא יכניס אותה ל-cache.
 *
 * הבדיקה מוגבלת ל-Content-Type של JSON בכוונה: endpoints שמחזירים טקסט או
 * קובץ (ר' ReceiptsController, כל מה שחוזר דרך File) עוברים כאן בלי לגעת בהם.
 *
 * ⚠️ HAT-1369: מחרוזת ב-data אינה לבדה הוכחה שהגוף לא נפרסר. גם JSON תקין יכול
 * להיות מחרוזת - calculate-payment-date מחזיר תאריך, "2026-10-05T10:21:40Z", ו-axios
 * מפרסר אותו כהלכה למחרוזת. כשהבדיקה זרקה גם עליו, התאריך לא הגיע אף פעם, מגבלת
 * הזכאות בשלב התשלום נשארה על "מחשב..." והרכזים הגישו מעל הזכאות בלי שום התראה.
 * ההבדל נראה רק בגוף הגולמי, ולכן ההכרעה נעשית עליו.
 */
const isParsableJson = (text: string) => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

const assertJsonBody = (response: AxiosResponse, url: string, responseType?: ResponseType) => {
  if (responseType && responseType !== 'json') return;
  if (typeof response.data !== 'string') return;
  // גוף ריק הוא תשובה לגיטימית (204 וכל 200 בלי תוכן); axios עצמו לא מנסה
  // לפרסר אותו, ולכן היעדר פירסור כאן אינו מעיד על כלום.
  if (response.data === '') return;
  const contentType = String(response.headers?.['content-type'] ?? '');
  if (!contentType.includes('json')) return;
  // בדפדפן response.request הוא ה-XMLHttpRequest, ו-responseText הוא הגוף כפי שהגיע.
  // בלעדיו אין דרך להבחין, ונשארים עם ההחלטה הזהירה של HAT-1343.
  const rawBody: unknown = response.request?.responseText;
  if (typeof rawBody === 'string' && isParsableJson(rawBody)) return;
  throw new Error(`Malformed JSON response from ${url} (status ${response.status}, ${response.data.length} chars)`);
};

export const get = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get<T>(url, params);
    assertJsonBody(response, url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}:`, error);
    throw error;
  }
};

export const post = async <T, P = object>(url: string, body: P, config?: object): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post<T>(url, body, config);
    return response.data;
  } catch (error) {
    console.error(`Unexpected error posting data to ${url}:`, error);
    throw error;
  }
};

export const put = async <T, P = object>(url: string, body: P): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put<T>(url, body);
    return response.data;
  } catch (error) {
    console.error(`Error putting data to ${url}:`, error);
    throw error;
  }
};

export const del = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete<T>(url, params);
    return response.data;
  } catch (error) {
    console.error(`Error deleting data from ${url}:`, error);
    throw error;
  }
};

export const getAuth = async <T>(url: string, headers: object, responseType?: ResponseType, signal?: AbortSignal): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get<T>(url, {
      headers,
      responseType,
      signal,
    });
    assertJsonBody(response, url, responseType);
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}:`, error);
    throw error;
  }
};

export const putAuth = async <T, P = object, B = object>(url: string, headers: object, params: P = {} as P, body: B = {} as B): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put<T>(url, body, {
      headers,
      params,
    });
    return response.data;
  } catch (error) {
    console.error(`Error putting data to ${url}:`, error);
    throw error;
  }
};

export const postAuth = async <T, P = object>(url: string, headers: object, body: P = {} as P, signal?: AbortSignal): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post<T>(url, body, { headers, signal });
    return response.data;
  } catch (error) {
    console.error(`Error posting data to ${url}:`, error);
    throw error;
  }
};

export const patchAuth = async <T, P = object>(url: string, body: P, headers: object): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.patch<T>(url, body, { headers });
    return response.data;
  } catch (error) {
    console.error(`Error patching data to ${url}:`, error);
    throw error;
  }
};

export const delAuth = async <T>(url: string, headers: object): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete<T>(url, { headers });
    return response.data;
  } catch (error) {
    console.error(`Error deleting data from ${url}:`, error);
    throw error;
  }
};
